<template>
  <div>
    <h1 class="mb-8 text-3xl font-bold">
      <inertia-link class="text-indigo-400 hover:text-indigo-600" :href="route('mahasiswa.krs.index')">
        KRS &amp; KHS
      </inertia-link>
      <span class="font-medium text-indigo-400">/</span>
      {{ mahasiswa.name }}
      <span class="font-medium text-indigo-400">/</span>
      Tahun: {{ report.school_year }}
      <span class="font-medium text-indigo-400">/</span>
      Semester: {{ report.school_semester }}
    </h1>
    <trashed-message
      v-if="report.deleted_at"
      class="mb-6"
      @restore="restore">
      This report KRS has been deleted.
    </trashed-message>
    <div class="overflow-hidden max-w-3xl bg-white rounded-md shadow">
      <form @submit.prevent="update(krs)">
        <h3 class="pl-8 mt-6 text-2xl font-bold">
          Kartu Rencana Studi
        </h3>
        <div class="flex flex-wrap p-8 -mr-6 -mb-2">
          <text-input
            v-model="krs.school_year"
            :error="krs.errors.school_year"
            class="pr-6 pb-8 w-full lg:w-1/3"
            label="Tahun Ajaran"
            :disabled="krsReadOnly" />
          <text-input
            v-model="krs.school_semester"
            :error="krs.errors.school_semester"
            class="pr-6 pb-8 w-full lg:w-1/3"
            label="Semester"
            :disabled="krsReadOnly" />
          <text-input
            v-model="krs.krs_sks"
            :error="krs.errors.krs_sks"
            class="pr-6 pb-6 w-full lg:w-1/3"
            label="SKS KRS"
            :disabled="krsReadOnly" />
          <file-input
            v-model="krs.krs_file"
            :error="krs.errors.krs_file"
            class="pr-6 pb-4 -mt-4 w-full lg:w-1/2"
            type="file"
            accept="image/*"
            label="Scan hasil KRS"
            :disabled="krsReadOnly" />
          <div class="items-start pr-6 mt-3 w-full md:-ml-4 lg:w-1/4">
            <button
              v-if="report.krs_file"
              class="btn-green"
              type="button"
              @click.prevent="showModal('krs_file')">
              Preview
            </button>
          </div>
          <div tabindex="0" class="mr-6 w-full bg-gray-200 border border-base-300">
            <div class="m-6 text-xl font-medium">
              Status verifikasi KRS :
              {{
                krs.krs_file_verified
                  ? `VERIF - ${report.krs_verif_fdate}`
                  : "BELUM VERIF"
              }}
            </div>
            <div class="m-6">
              <p class="my-2">
                - Operator Verifikasi:
                {{ report.krs_verif_operator }}
              </p>
              <p class="my-2">
                - Tanggal Verifikasi:
                {{ report.krs_verified_date }}
              </p>
              <p class="my-2">
                - Keterangan: {{ report.krs_file_reason }}
              </p>
            </div>
          </div>
        </div>
        <div v-if="!krsReadOnly" class="flex items-center py-4 px-8 my-2 bg-gray-50 border-t border-gray-100">
          <loading-button
            :loading="krs.processing"
            class="ml-auto btn-indigo"
            type="submit">
            Update KRS
          </loading-button>
        </div>
      </form>
      <div class="divider" />
      <form @submit.prevent="update(khs)">
        <h3 class="pl-8 text-2xl font-bold">
          Kartu Hasil Studi
        </h3>
        <div class="flex flex-wrap p-8 mb-2 -mr-6">
          <text-input
            v-model="khs.ip"
            :error="khs.errors.ip"
            class="pr-6 pb-8 w-full lg:w-1/3"
            label="IP Semester (GPA)"
            :disabled="khsReadOnly" />
          <text-input
            v-model="khs.ipk"
            :error="khs.errors.ipk"
            class="pr-6 pb-8 w-full lg:w-1/3"
            label="IP Kumulatif (CGPA)"
            :disabled="khsReadOnly" />
          <text-input
            v-model="khs.khs_sks"
            :error="khs.errors.khs_sks"
            class="pr-6 pb-6 w-full lg:w-1/3"
            label="SKS KHS"
            :disabled="khsReadOnly" />
          <file-input
            v-model="khs.khs_file"
            :error="khs.errors.khs_file"
            class="pr-6 pb-4 -mt-4 w-full lg:w-1/2"
            type="file"
            accept="image/*"
            label="Scan hasil KHS"
            :disabled="khsReadOnly" />
          <div class="items-start pr-6 mt-3 w-full md:-ml-4 lg:w-1/4">
            <button
              v-if="report.khs_file"
              class="btn-green"
              type="button"
              @click.prevent="showModal('khs_file')">
              Preview
            </button>
          </div>
          <div tabindex="0" class="mr-6 w-full bg-gray-200 border border-base-300">
            <div class="m-6 text-xl font-medium">
              Status verifikasi KHS :
              {{
                khs.khs_file_verified
                  ? `VERIF - ${report.khs_verif_fdate}`
                  : "BELUM VERIF"
              }}
            </div>
            <div class="m-6">
              <p class="my-2">
                - Operator Verifikasi:
                {{ report.khs_verif_operator }}
              </p>
              <p class="my-2">
                - Tanggal Verifikasi:
                {{ report.khs_verified_date }}
              </p>
              <p class="my-2">
                - Keterangan: {{ report.khs_file_reason }}
              </p>
            </div>
          </div>
        </div>
        <div v-if="!khsReadOnly" class="flex items-center py-4 px-8 my-2 bg-gray-50 border-t border-gray-100">
          <loading-button
            :loading="khs.processing"
            class="ml-auto btn-indigo"
            type="submit">
            Update KHS
          </loading-button>
        </div>
      </form>
    </div>
    <modal :is-modal-show="isModalVisible" @close="closeModal">
      <template slot="header">
        {{ modalTextHeader }}
      </template>
      <template slot="body">
        <div class="flex justify-center">
          <blob-image :src="imgUrl" />
        </div>
      </template>
    </modal>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'
import FileInput from '@/Shared/FileInput'
import Modal from '@/Shared/Modal'
import BlobImage from '@/Shared/BlobImage'
import Swal from 'sweetalert2'
import { useForm, Link } from '@inertiajs/vue2'

export default {
    metaInfo () {
        return {
            title: `${this.mahasiswa.name}`,
        }
    },
    components: {
        LoadingButton,
        TextInput,
        TrashedMessage,
        FileInput,
        Modal,
        BlobImage,
        'inertia-link': Link,
    },
    layout: Layout,
    props: {
        report: Object,
        mahasiswa: Object,
        isInputKrsClosed: Boolean,
    },
    remember: 'form',
    data () {
        return {
            imgUrl: null,
            isModalVisible: false,
            modalTextHeader: 'Hasil scan',
            krs: useForm({
                school_year: this.report.school_year.toString(),
                school_semester: this.report.school_semester.toString(),
                krs_sks: this.report.krs_sks.toString(),
                krs_file: null,
                krs_file_reason: this.report.krs_file_reason,
                krs_file_verified: this.report.krs_file_verified,
                _utype: 'krs',
            }),
            khs: useForm({
                ip: this.report.ip,
                ipk: this.report.ipk,
                khs_sks: this.report.khs_sks
                    ? this.report.khs_sks.toString()
                    : '0',
                khs_file: null,
                khs_file_reason: this.report.khs_file_reason,
                khs_file_verified: this.report.khs_file_verified,
                _utype: 'khs',
            }),
        }
    },
    computed: {
        krsReadOnly () {
            return this.report.krs_file_verified === 1
        },
        khsReadOnly () {
            return this.report.khs_file_verified === 1
        },
    },
    methods: {
        update (form) {
            if (form._utype === 'khs' && !this.report.khs_file && !form.khs_file) {
                Swal.fire(
                    'Gagal Input KHS',
                    'File scan KHS wajib disertakan saat menginput KHS.',
                    'error',
                )
                return
            }

            if (this.isInputKrsClosed)
                Swal.fire(
                    'Gagal Input KRS/KHS',
                    'Periode penginputan sudah di tutup.',
                    'error',
                )
            else {
                form.post(this.route('mahasiswa.krs.update', this.report.id), {
                    onSuccess: () => {
                        this.krs.reset('krs_file')
                        this.khs.reset('khs_file')
                    },
                    preserveScroll: true,
                })
            }
        },
        showModal (docType) {
            this.imgUrl =
                docType === 'krs_file'
                    ? `/preview/${this.report.krs_file}`
                    : `/preview/${this.report.khs_file}`
            this.modalTextHeader =
                docType === 'krs_file' ? 'Gambar scan KRS' : 'Gambar scan KHS'
            this.isModalVisible = true
        },
        closeModal () {
            this.isModalVisible = false
        },
    },
}
</script>
